import type { GlobalCataloguesCatalogueDto } from '@ori/global-api-client/esm/models';
import type { StaticCataloguesCatalogueDto } from '@ori/static-api-client/esm/models';
import type { FC } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useApiProviderContext } from '~/contexts/ApiProviderContext';
import { useAppConfigurationContext } from '~/contexts/AppConfigurationContext';

interface CataloguesContextProps {
  catalogues?: GlobalCataloguesCatalogueDto[];
  isDataFetched: boolean;
}

export const CataloguesContext = createContext<CataloguesContextProps | undefined>(undefined);

export const CataloguesContextProvider: FC = ({ children }) => {
  const { tenant } = useAppConfigurationContext();
  const [catalogues, setCatalogues] = useState<StaticCataloguesCatalogueDto[] | undefined>(
    undefined,
  );
  const [isDataFetched, setIsDataFetched] = useState(false);
  const { staticApiClient } = useApiProviderContext();

  const getCatalogues = useCallback(async () => {
    setIsDataFetched(true);
    const { collection } = await staticApiClient.getTenantCatalogues(tenant);

    return collection;
  }, [staticApiClient, tenant]);

  useEffect(() => {
    if (!isDataFetched) {
      getCatalogues().then(setCatalogues);
    }
  }, [getCatalogues, isDataFetched, staticApiClient]);

  const value: CataloguesContextProps = useMemo(
    () => ({
      catalogues,
      isDataFetched,
    }),
    [catalogues, isDataFetched],
  );

  return <CataloguesContext.Provider value={value}>{children}</CataloguesContext.Provider>;
};

export const useCataloguesContext = () => {
  const context = useContext(CataloguesContext);
  if (context) {
    return context;
  }
  throw new Error('CataloguesContext value must be set.');
};
